import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Trash2, Plus, X, Save, AlertTriangle, Package, TrendingDown,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const WASTE_CATS = ['spoilage', 'prep_error', 'staff_meal', 'cancelled_order', 'comp', 'expired', 'other'];
const WASTE_COLORS = {
  spoilage: 'bg-red-500/10 text-red-600', prep_error: 'bg-orange-500/10 text-orange-600',
  staff_meal: 'bg-blue-500/10 text-blue-600', cancelled_order: 'bg-purple-500/10 text-purple-600',
  comp: 'bg-teal-500/10 text-teal-600', expired: 'bg-amber-500/10 text-amber-600',
  other: 'bg-gray-500/10 text-gray-600',
};

export default function WasteTracker({ branchId, token, t, lang, onSummaryReady }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState('month');
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/accounting/waste?branch_id=${branchId}&page=${page}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.items || []);
      setPages(data.pages || 1);
    } catch { toast.error(t('common.failed')); }
    setLoading(false);
  }, [branchId, token, page, t]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/accounting/waste/summary?branch_id=${branchId}&period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setSummary(data);
      if (onSummaryReady) onSummaryReady(data);
    } catch {}
  }, [branchId, token, period, onSummaryReady]);

  useEffect(() => { if (branchId) fetchItems(); }, [fetchItems, branchId]);
  useEffect(() => { if (branchId) fetchSummary(); }, [fetchSummary, branchId]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('waste.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/api/accounting/waste/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('waste.deleted'));
      fetchItems();
      fetchSummary();
    } catch { toast.error(t('common.failed')); }
  };

  return (
    <div className="space-y-5" data-testid="waste-tracker-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('waste.totalCost')}</p>
            <p className="text-xl font-bold text-red-500">{fmt(summary.total_waste_cost)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('waste.totalEntries')}</p>
            <p className="text-xl font-bold text-foreground">{summary.total_entries}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('waste.pctRevenue')}</p>
            <p className={`text-xl font-bold ${summary.waste_pct_of_revenue > 3 ? 'text-red-500' : summary.waste_pct_of_revenue > 1 ? 'text-amber-500' : 'text-emerald-500'}`}>{summary.waste_pct_of_revenue}%</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('waste.categories')}</p>
            <p className="text-xl font-bold text-foreground">{summary.by_category?.length || 0}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="add-waste-btn">
          <Plus className="w-4 h-4 me-2" /> {t('waste.add')}
        </Button>
        <div className="flex gap-1 ms-auto">
          {['week', 'month', 'year'].map(p => (
            <Button key={p} variant={period === p ? 'default' : 'outline'} size="sm" onClick={() => setPeriod(p)}
              className={period === p ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0' : ''}>
              {t(`accounting.${p}`)}
            </Button>
          ))}
        </div>
      </div>

      {/* By Category Breakdown */}
      {summary?.by_category?.length > 0 && (
        <Card data-testid="waste-by-category">
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3">{t('waste.byCategory')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {summary.by_category.map(c => (
                <div key={c.category} className={`p-3 rounded-xl ${WASTE_COLORS[c.category] || 'bg-gray-100'}`}>
                  <span className="text-xs font-medium">{t(`waste.${c.category}`)}</span>
                  <p className="text-lg font-bold">{fmt(c.total_cost)}</p>
                  <p className="text-xs opacity-70">{c.count} {t('flash.entries')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Wasted Items */}
      {summary?.top_items?.length > 0 && (
        <Card data-testid="waste-top-items">
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3">{t('waste.topWasted')}</h3>
            <div className="space-y-2">
              {summary.top_items.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-red-500/10 text-red-600 text-xs font-bold flex items-center justify-center">{i+1}</span>
                    <span>{item.name || '-'}</span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-muted-foreground">{item.count}x</span>
                    <span className="font-bold text-red-500">{fmt(item.total_cost)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Modal */}
      {showForm && <WasteForm branchId={branchId} token={token} t={t} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchItems(); fetchSummary(); }} />}

      {/* Recent Entries */}
      {loading ? <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div> : items.length === 0 ? (
        <div className="text-center py-12" data-testid="waste-empty">
          <Trash2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">{t('waste.noEntries')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          <h3 className="font-semibold text-sm">{t('waste.recentEntries')}</h3>
          {items.map(w => (
            <Card key={w.id} className="hover:shadow-sm transition-shadow" data-testid={`waste-row-${w.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${WASTE_COLORS[w.category] || 'bg-gray-100'}`}>
                  <Trash2 className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{w.item_name || t(`waste.${w.category}`)}</span>
                    <Badge variant="outline" className="text-[10px] py-0">{t(`waste.${w.category}`)}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{w.reason || '-'} | {w.quantity} {w.unit}</p>
                </div>
                <div className="text-end">
                  <p className="text-sm font-bold text-red-500">{fmt(w.estimated_cost)}</p>
                  <p className="text-[10px] text-muted-foreground">{w.date}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(w.id)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
          {pages > 1 && (
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p-1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="flex items-center text-sm text-muted-foreground px-3">{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p+1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WasteForm({ branchId, token, t, onClose, onSaved }) {
  const [form, setForm] = useState({
    category: 'spoilage', item_name: '', quantity: 1, unit: 'piece',
    estimated_cost: '', reason: '', date: new Date().toISOString().split('T')[0],
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.estimated_cost || parseFloat(form.estimated_cost) <= 0) { toast.error(t('waste.enterCost')); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/accounting/waste`, { ...form, branch_id: branchId, estimated_cost: parseFloat(form.estimated_cost), quantity: parseFloat(form.quantity) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('waste.added'));
      onSaved();
    } catch { toast.error(t('common.failed')); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="waste-form-modal">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">{t('waste.add')}</h2>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('accounting.category')}</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
                <SelectTrigger data-testid="waste-category"><SelectValue /></SelectTrigger>
                <SelectContent>{WASTE_CATS.map(c => <SelectItem key={c} value={c}>{t(`waste.${c}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('waste.itemName')}</label>
              <Input value={form.item_name} onChange={e => setForm(f => ({...f, item_name: e.target.value}))} data-testid="waste-item-name" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('waste.qty')}</label>
              <Input type="number" value={form.quantity} onChange={e => setForm(f => ({...f, quantity: e.target.value}))} data-testid="waste-qty" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('waste.unit')}</label>
              <Select value={form.unit} onValueChange={v => setForm(f => ({...f, unit: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['piece', 'kg', 'liter', 'plate'].map(u => <SelectItem key={u} value={u}>{t(`waste.${u}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('waste.cost')}</label>
              <Input type="number" value={form.estimated_cost} onChange={e => setForm(f => ({...f, estimated_cost: e.target.value}))} placeholder="0.00" data-testid="waste-cost" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t('waste.reason')}</label>
            <Input value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))} data-testid="waste-reason" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t('accounting.date')}</label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} data-testid="waste-date" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="save-waste-btn">
              <Save className="w-4 h-4 me-2" /> {t('common.save')}
            </Button>
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
